import { ColorLabel } from 'ui/components/elements/ColorLabel/ColorLabel';
import { IIndustry } from 'domain/entities/Industry/Industry';
import dayjs from 'dayjs';
import styled from '@emotion/styled';
import { MATCH_STATUSES } from 'domain/entities/Match/Match';
import { mqMin } from 'utils/responsive';
import { SubTitleLabel } from 'ui/components/elements/ColorLabel/SubTitleLabel';

const Wrapper = styled.div<{ isPreview?: boolean }>`
    position: relative;
    width: 100%;
    margin: ${(props) => (props.isPreview ? '0 auto 20px 0' : '0 auto 100px auto')};
    height: auto;
    background-color: var(--white);
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    min-width: 320px;
    min-height: 330px;

    ${mqMin('NARROW')} {
        max-width: 800px;
    }
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 30px;
    padding: 30px;
`;

const SectionContainer = styled.div`
    background: linear-gradient(135deg, #f8f9fd 0%, #ffffff 100%);
    border-radius: 12px;
    padding: 25px;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    border: 1px solid #e8eaed;
    white-space: pre-line;
    word-break: break-all;
`;

const SectionTitle = styled.div`
    font-size: var(--font-x-large);
    font-weight: var(--font-bold);
    color: var(--dark-blue);
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 2px solid var(--main-color-blue);
    position: relative;

    &::before {
        content: '';
        position: absolute;
        left: 0;
        bottom: -2px;
        width: 30px;
        height: 2px;
        background-color: var(--main-color-blue);
    }
`;

const HorizontalContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const Item = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
`;

const H2Title = styled.h2`
    font-size: var(--font-xx-large);
    font-weight: var(--font-bold);
    color: var(--dark-blue);
    margin-top: 5px;
    margin-bottom: 20px;
    gap: 5px;
    display: flex;
    flex-direction: column;
    font-weight: 800;
    line-height: 1.2;
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 15px;
    margin-bottom: 15px;
    padding: 0 20px 10px 20px;
`;

const InfoItem = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
`;

const InfoLabel = styled.div`
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    color: #666;
    min-width: 120px;
`;

const InfoValue = styled.div`
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    color: var(--dark-blue);
`;

const TextArea = styled.div`
    font-size: var(--font-large);
    padding: 5px;
    border-radius: 10px;

    p {
        line-height: 1.5;
        margin: 0;
    }
`;

const TextItemContainer = styled.div`
    gap: 5px;
    display: flex;
    flex-direction: column;
`;

const QuestionTitle = styled.div`
    font-size: var(--font-large);
    font-weight: var(--font-bold);
    margin-top: 10px;
    margin-left: 5px;
    padding-left: 25px;
    position: relative;
    color: var(--dark-blue);

    &::before {
        content: '';
        position: absolute;
        left: 0px;
        top: 50%;
        width: 18px;
        height: 18px;
        background-color: var(--dark-blue);
        border-radius: 50%;
        transform: translateY(-50%);
    }
`;

const CreatedAt = styled.div`
    font-size: var(--font-small);
    color: var(--text-color-gray);
    margin-left: auto;
`;

const CompanyLabel = styled.div`
    border-radius: 8px;
    padding: 0.5rem 0.825rem;
    font-weight: 600;
    font-size: var(--font-large);
    line-height: 1;
    letter-spacing: 0.025em;
    white-space: nowrap;
    width: fit-content;
    color: white;
    background: var(--main-color-blue);
    display: flex;
    align-items: center;
    justify-content: center;
`;

const StatusBadge = styled.div`
    border-radius: 30px;
    padding: 0.5rem 0.825rem;
    font-weight: 700;
    line-height: 14.4px;
    letter-spacing: 0%;
    white-space: nowrap;
    margin-right: auto;
    color: white;
    background-color: #ee793f;
    font-size: 12px;
    font-size: var(--font-medium);
`;

const SubtitleContainer = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 20px 10px 0 30px;
    margin-bottom: -10px;
`;

const Subtitle = styled.div`
    font-size: var(--font-x-large);
    font-weight: var(--font-bold);

    span {
        font-size: var(--font-medium);
        color: #696969;
    }
`;

interface PublicCompanyDetailProps {
    company: any;
    areaChoices: string[][];
    prefectureChoices: string[][];
    employeeChoices: string[][];
    revenueChoices: string[][];
    profitLevelChoices: string[][];
    netAssetsLevelChoices: string[][];
    industryChoices: IIndustry[];
    matchStatus?: string;
    isPreview?: boolean;
}

export const PublicCompanyDetail: React.FC<PublicCompanyDetailProps> = ({
    company,
    areaChoices,
    prefectureChoices,
    employeeChoices,
    revenueChoices,
    profitLevelChoices,
    netAssetsLevelChoices,
    industryChoices,
    matchStatus,
    isPreview,
}) => {
    const getLabel = (choices: string[][], key: string) => {
        const match = choices.find(([value]) => value === key);
        return match ? match[1] : key;
    };

    const getIndustryLabel = (id: string) => {
        const match = industryChoices.find((i) => i.id.toString() === id);
        return match ? match.name : id;
    };

    return (
        <Wrapper isPreview={isPreview}>
            {isPreview && (
                <SubtitleContainer>
                    <Subtitle>
                        プレビュー<span>（マッチング前に公開する画面）</span>
                    </Subtitle>
                </SubtitleContainer>
            )}
            <ContentWrapper>
                <SectionContainer>
                    <SectionTitle>
                        <HorizontalContainer>
                            <Item>{company?.label && <CompanyLabel>{company.label}</CompanyLabel>}</Item>
                            {matchStatus && (
                                <StatusBadge>
                                    {matchStatus === MATCH_STATUSES.MATCH_STATUS_WAITING_ACCEPT && 'オファー済み'}
                                    {matchStatus === MATCH_STATUSES.MATCH_STATUS_CONNECTED && 'マッチング済み'}
                                    {matchStatus === MATCH_STATUSES.MATCH_STATUS_EXPIRED && 'オファー期限切れ'}
                                    {matchStatus === MATCH_STATUSES.MATCH_STATUS_DECLINED && 'オファー辞退'}
                                </StatusBadge>
                            )}
                            <CreatedAt>登録日：{dayjs(company.created_at).format('YYYY/MM/DD')}</CreatedAt>
                        </HorizontalContainer>
                    </SectionTitle>

                    {company.is_sole_proprietor && (
                        <div style={{ marginBottom: '5px' }}>
                            <ColorLabel color="#008000" backgroundColor="#d3ffa8">
                                個人事業主
                            </ColorLabel>
                        </div>
                    )}
                    <Item>
                        <H2Title>{getIndustryLabel(company.industry)}</H2Title>
                    </Item>

                    <InfoGrid>
                        <InfoItem>
                            <InfoLabel>エリア</InfoLabel>
                            <InfoValue>
                                {getLabel(areaChoices, company.area)} /{' '}
                                {getLabel(prefectureChoices, company.prefecture)}
                            </InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>従業員規模</InfoLabel>
                            <InfoValue>{getLabel(employeeChoices, company.number_of_employees)}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>売上高</InfoLabel>
                            <InfoValue>{getLabel(revenueChoices, company.revenue_scale)}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>営業利益</InfoLabel>
                            <InfoValue>{getLabel(profitLevelChoices, company.profit_level)}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>純資産金額</InfoLabel>
                            <InfoValue>{getLabel(netAssetsLevelChoices, company.net_assets_level)}</InfoValue>
                        </InfoItem>
                    </InfoGrid>

                    <TextItemContainer>
                        <SubTitleLabel>事業内容</SubTitleLabel>
                        <TextArea style={{ whiteSpace: 'pre-line' }}>{company.business_description}</TextArea>
                    </TextItemContainer>
                </SectionContainer>
                {(company.fee_structure ||
                    company.timing_considerations ||
                    company.matching_potential ||
                    company.case_studies ||
                    company.question) && (
                    <SectionContainer>
                        <TextItemContainer>
                            <SubTitleLabel>仲介会社に聞いてみたいこと</SubTitleLabel>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {company.fee_structure && (
                                    <>
                                        <QuestionTitle>M&Aに関する手数料水準等について</QuestionTitle>
                                        {company.fee_structure_txt && (
                                            <TextArea style={{ whiteSpace: 'pre-line' }}>
                                                {company.fee_structure_txt}
                                            </TextArea>
                                        )}
                                    </>
                                )}
                                {company.timing_considerations && (
                                    <>
                                        <QuestionTitle>M&Aを検討し始めるタイミングについて</QuestionTitle>
                                        {company.timing_considerations_txt && (
                                            <TextArea style={{ whiteSpace: 'pre-line' }}>
                                                {company.timing_considerations_txt}
                                            </TextArea>
                                        )}
                                    </>
                                )}
                                {company.matching_potential && (
                                    <>
                                        <QuestionTitle>自社のマッチング可能性について</QuestionTitle>
                                        {company.matching_potential_txt && (
                                            <TextArea style={{ whiteSpace: 'pre-line' }}>
                                                {company.matching_potential_txt}
                                            </TextArea>
                                        )}
                                    </>
                                )}
                                {company.case_studies && (
                                    <>
                                        <QuestionTitle>他社事例などについて</QuestionTitle>
                                        {company.case_studies_txt && (
                                            <TextArea style={{ whiteSpace: 'pre-line' }}>
                                                {company.case_studies_txt}
                                            </TextArea>
                                        )}
                                    </>
                                )}
                                {company.process_flow && (
                                    <>
                                        <QuestionTitle>M&Aの具体的な流れについて</QuestionTitle>
                                        {company.process_flow_txt && (
                                            <TextArea style={{ whiteSpace: 'pre-line' }}>
                                                {company.process_flow_txt}
                                            </TextArea>
                                        )}
                                    </>
                                )}
                                {company.question && (
                                    <>
                                        <QuestionTitle>その他の質問や要望</QuestionTitle>
                                        <TextArea style={{ whiteSpace: 'pre-line' }}>{company.question}</TextArea>
                                    </>
                                )}
                            </div>
                        </TextItemContainer>
                    </SectionContainer>
                )}
            </ContentWrapper>
        </Wrapper>
    );
};
